import React from 'react';
import { Image, Tag, Typography } from 'antd';

const { Text, Title, Paragraph } = Typography;
const markdownImageRegex = /^!\[([^\]]*)\]\(([^)]+)\)$/;

const isSafeImageUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    return ['http:', 'https:'].includes(parsedUrl.protocol);
  } catch {
    return false;
  }
};

const parseMarkdownImage = (line) => {
  const content = line.replace(/^([-*]\s+|•\s*|\d{1,3}\.\s*)/, '').trim();
  const match = content.match(markdownImageRegex);

  if (!match) return null;

  const src = match[2].trim();
  if (!isSafeImageUrl(src)) return null;

  return {
    alt: match[1]?.trim() || 'Image',
    src,
  };
};

// Helper function to format temperatures and percentages
const formatSpecialValues = (text) => {
  const parts = [];
  let lastIndex = 0;

  // Temperature regex
  const tempRegex = /(\d+\.?\d*)°C/g;
  let tempMatch;

  while ((tempMatch = tempRegex.exec(text)) !== null) {
    // Add text before temperature
    if (tempMatch.index > lastIndex) {
      const beforeText = text.substring(lastIndex, tempMatch.index);
      if (beforeText) parts.push(beforeText);
    }

    // Add temperature tag
    parts.push(
      <Tag key={`temp-${tempMatch.index}`} color="blue" style={{ fontSize: '10px', margin: '0 2px' }}>
        {tempMatch[1]}°C
      </Tag>
    );

    lastIndex = tempMatch.index + tempMatch[0].length;
  }

  // Add remaining text and check for percentages
  let remainingText = text.substring(lastIndex);

  // Percentage regex
  const percentRegex = /(\d+)%/g;
  let percentMatch;
  let percentLastIndex = 0;

  while ((percentMatch = percentRegex.exec(remainingText)) !== null) {
    // Add text before percentage
    if (percentMatch.index > percentLastIndex) {
      const beforePercent = remainingText.substring(percentLastIndex, percentMatch.index);
      if (beforePercent) parts.push(beforePercent);
    }

    // Add percentage tag
    parts.push(
      <Tag key={`percent-${percentMatch.index}`} color="green" style={{ fontSize: '10px', margin: '0 2px' }}>
        {percentMatch[1]}%
      </Tag>
    );

    percentLastIndex = percentMatch.index + percentMatch[0].length;
  }

  // Add final remaining text
  const finalText = remainingText.substring(percentLastIndex);
  if (finalText) parts.push(finalText);

  return parts.length > 0 ? parts : [text];
};

// Helper function to format inline markdown (bold, temperatures, percentages)
const formatInlineMarkdown = (text) => {
  const parts = [];
  let currentIndex = 0;

  // Process bold text (**text**)
  const boldRegex = /\*\*(.*?)\*\*/g;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    // Add text before bold
    if (match.index > currentIndex) {
      const beforeText = text.substring(currentIndex, match.index);
      parts.push(...formatSpecialValues(beforeText));
    }

    // Add bold text
    parts.push(
      <Text key={`bold-${match.index}`} strong style={{ color: '#1890ff' }}>
        {match[1]}
      </Text>
    );

    currentIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (currentIndex < text.length) {
    const remainingText = text.substring(currentIndex);
    parts.push(...formatSpecialValues(remainingText));
  }

  return parts.length > 0 ? parts : formatSpecialValues(text);
};

// Component to format bot messages with rich UI and Markdown support
const FormattedBotMessage = ({ text }) => {
  const lines = String(text || '').split('\n');

  return (
    <Image.PreviewGroup>
      <div>
        {lines.map((line, index) => {
          const trimmedLine = line.trim();

          // Skip empty lines
          if (!trimmedLine) {
            return <div key={index} style={{ height: '8px' }} />;
          }

          // Some GPT responses contain standalone bullet glyphs before markdown image lines.
          if (trimmedLine === '•') {
            return null;
          }

          const markdownImage = parseMarkdownImage(trimmedLine);
          if (markdownImage) {
            return (
              <div
                key={index}
                style={{
                  display: 'inline-flex',
                  margin: '6px 8px 8px 16px',
                  verticalAlign: 'top',
                }}
              >
                <Image
                  width={78}
                  height={78}
                  src={markdownImage.src}
                  alt={markdownImage.alt}
                  style={{
                    objectFit: 'cover',
                    borderRadius: 6,
                    border: '1px solid #d9d9d9',
                    cursor: 'pointer',
                    backgroundColor: '#f5f5f5',
                  }}
                  preview={{ mask: null }}
                />
              </div>
            );
          }

          // H3 Headers (###)
          if (trimmedLine.startsWith('### ')) {
            return (
              <Title
                key={index}
                level={4}
                style={{
                  margin: '12px 0 8px 0',
                  fontSize: '14px',
                  color: '#1890ff',
                }}
              >
                {trimmedLine.substring(4)}
              </Title>
            );
          }

          // H4 Headers (####)
          if (trimmedLine.startsWith('#### ')) {
            return (
              <Title
                key={index}
                level={5}
                style={{
                  margin: '10px 0 6px 0',
                  fontSize: '12px',
                  color: '#52c41a',
                }}
              >
                {trimmedLine.substring(5)}
              </Title>
            );
          }

          // Numbered lists (1. 2. 3.)
          const numberedListMatch = trimmedLine.match(/^(\d{1,3})\.\s*(.+)$/);
          if (numberedListMatch) {
            const content = numberedListMatch[2];
            return (
              <div key={index} style={{ marginLeft: '16px', marginBottom: '6px' }}>
                <Text style={{ fontSize: '11px' }}>
                  <Text strong style={{ color: '#fa8c16', marginRight: '8px' }}>
                    {numberedListMatch[1]}.
                  </Text>
                  {formatInlineMarkdown(content)}
                </Text>
              </div>
            );
          }

          // Bullet points (-, * or •)
          const bulletListMatch = trimmedLine.match(/^[-*•]\s+(.+)$/);
          if (bulletListMatch) {
            const content = bulletListMatch[1];
            return (
              <div
                key={index}
                style={{
                  marginLeft: '16px',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <Text style={{ color: '#1890ff', marginRight: '8px', fontSize: '11px', lineHeight: '16px' }}>•</Text>
                <Text style={{ fontSize: '11px', lineHeight: '16px' }}>{formatInlineMarkdown(content)}</Text>
              </div>
            );
          }

          // Regular paragraphs with inline formatting
          return (
            <Paragraph key={index} style={{ margin: '4px 0', fontSize: '11px', lineHeight: '16px' }}>
              {formatInlineMarkdown(trimmedLine)}
            </Paragraph>
          );
        })}
      </div>
    </Image.PreviewGroup>
  );
};

export default FormattedBotMessage;
